// src/hooks/useWebSocket.js
import { useState, useEffect, useRef, useCallback } from 'react';
import { getFcmToken } from '../services/firebaseService'; // Import getFcmToken

const useWebSocket = (deviceId, fcmToken, setProcessMessageFromApp, setBrowserNotificationPermission) => { // Renamed setProcessMessage to setProcessMessageFromApp to avoid confusion
    const [ws, setWs] = useState(null);
    const [connectionStatus, setConnectionStatus] = useState('Initializing...');
    const [loggedIn, setLoggedIn] = useState(false);
    const [loggedInPhone, setLoggedInPhone] = useState('');
    const [messages, setMessages] = useState([]);
    const [isNotificationsEnabled, setIsNotificationsEnabled] = useState(null);
    const [logincode, setLogincode] = useState('');
    const [step, setStep] = useState('connect');
    const [attemptsLeft, setAttemptsLeft] = useState(3);
    const wsRef = useRef(null);
    const reconnectAttempts = useRef(0);
    const reconnectTimeoutId = useRef(null);
    const messageTimeoutId = useRef(null);

    const getFcmTokenHook = useCallback(async () => {
        return getFcmToken(setProcessMessageFromApp, (token) => { /* no-op or handle if fcmToken state is in this hook */ }, setBrowserNotificationPermission);
    }, [setProcessMessageFromApp, setBrowserNotificationPermission]);


    const connectWebSocket = useCallback(() => {
        if (!deviceId) {
            console.log("DeviceId not ready for WebSocket connection.");
            return;
        }
        if (wsRef.current && wsRef.current.readyState !== WebSocket.CLOSED) {
            console.log("WebSocket already active. Closing existing connection.");
            wsRef.current.close();
            clearTimeout(reconnectTimeoutId.current);
        }

        setConnectionStatus('Connecting...');
        setProcessMessageFromApp('Establishing WebSocket connection...'); // Use the passed setter

        const socket = new WebSocket('wss://wappsync.com:8080');

        socket.onopen = () => {
            setConnectionStatus('✅ Connected');
            setProcessMessageFromApp(''); // Use the passed setter
            console.log('WebSocket opened. Sending initial message with fcmToken state:', fcmToken);
            socket.send(JSON.stringify({ deviceid: deviceId, fcm: fcmToken }));
            setWs(socket);
            wsRef.current = socket;
            reconnectAttempts.current = 0;
            clearTimeout(reconnectTimeoutId.current);
        };

        socket.onmessage = (event) => {
            const msg = JSON.parse(event.data);
            console.log('Received:', msg);

            if (msg.loggedin === false) {
                setStep('phone');
                setLoggedIn(false);
                setLoggedInPhone('');
                setMessages([]);
                setProcessMessageFromApp(''); // Use the passed setter
                setIsNotificationsEnabled(null);
            } else if (msg.loggedin === true) {
                setLoggedIn(true);
                setStep('done');
                if (msg.phone) {
                    setLoggedInPhone(msg.phone);
                }
                setProcessMessageFromApp('✅ Login successful!'); // Use the passed setter
                if (messageTimeoutId.current) {
                    clearTimeout(messageTimeoutId.current);
                }
                messageTimeoutId.current = setTimeout(() => {
                    setProcessMessageFromApp(''); // Use the passed setter
                }, 5000);

                if (typeof msg.notifications_enabled === 'boolean') {
                    if (Notification.permission === 'granted') {
                        setIsNotificationsEnabled(msg.notifications_enabled);
                    } else {
                        setIsNotificationsEnabled(false);
                    }
                }
            } else if (msg.phone) {
                setLoggedInPhone(msg.phone);
            } else if (msg.logincode) {
                setLogincode(msg.logincode);
                setStep('otp');
                setAttemptsLeft(3);
                setProcessMessageFromApp(''); // Use the passed setter
            } else if (msg.success !== undefined) {
                if (msg.success) {
                    setProcessMessageFromApp('✅ Login successful!'); // Use the passed setter
                    setLoggedIn(true);
                    setStep('done');
                    if (loggedInPhone) setLoggedInPhone(loggedInPhone);
                    if (messageTimeoutId.current) {
                        clearTimeout(messageTimeoutId.current);
                    }
                    messageTimeoutId.current = setTimeout(() => {
                        setProcessMessageFromApp(''); // Use the passed setter
                    }, 5000);
                } else {
                    setAttemptsLeft(prev => {
                        const next = prev - 1;
                        setProcessMessageFromApp(`❌ Login failed. ${next} attempt(s) left.`); // Use the passed setter
                        return next;
                    });
                }
            } else if (msg.error) {
                setProcessMessageFromApp(`❌ Error: ${msg.error}`); // Use the passed setter
            } else if (msg.type === 'new_messages' && Array.isArray(msg.messages)) {
                setMessages(prevMessages => {
                    const combined = [...prevMessages, ...msg.messages];
                    const uniqueMessagesMap = new Map();
                    combined.forEach(m => uniqueMessagesMap.set(m.id, m));
                    const uniqueMessages = Array.from(uniqueMessagesMap.values());
                    // Sort messages by timestamp in descending order (newest first)
                    return uniqueMessages.sort((a, b) => b.timestamp - a.timestamp);
                });
            } else if (msg.type === 'notifications_status' && typeof msg.enabled === 'boolean') {
                if (Notification.permission === 'granted') {
                    setIsNotificationsEnabled(msg.enabled);
                } else {
                    setIsNotificationsEnabled(false);
                }
                console.log('Received notification status from server:', msg.enabled ? 'Enabled' : 'Disabled');
            } else if (msg.type === 'notifications_status_update') {
                if (msg.success) {
                    if (Notification.permission === 'granted') {
                        setIsNotificationsEnabled(msg.enabled);
                        setProcessMessageFromApp(`Notifications ${msg.enabled ? 'enabled' : 'disabled'} on server.`); // Use the passed setter
                    } else {
                        setIsNotificationsEnabled(false);
                        setProcessMessageFromApp(`Notifications ${msg.enabled ? 'enabled' : 'disabled'} on server, but browser permission is required.`); // Use the passed setter
                    }
                    console.log(`Notifications ${msg.enabled ? 'enabled' : 'disabled'} on server.`);
                } else {
                    setProcessMessageFromApp(`Failed to update notification status: ${msg.error || 'Unknown error'}`); // Use the passed setter
                    console.error(`Failed to update notification status: ${msg.error || 'Unknown error'}`);
                }
            }
        };

        socket.onerror = (e) => {
            setConnectionStatus('❌ Error');
            setProcessMessageFromApp('❌ WebSocket connection error. Please try reconnecting.'); // Use the passed setter
            console.error('WebSocket Error:', e);
            socket.close();
        };

        socket.onclose = async () => {
            setConnectionStatus('🔌 Disconnected');
            setProcessMessageFromApp('🔌 Disconnected. Attempting to reconnect...'); // Use the passed setter
            setWs(null);
            wsRef.current = null;

            if (Notification.permission === 'granted') {
                console.log('Disconnected. Re-fetching FCM token for reconnect...');
                await getFcmTokenHook();
            } else {
                console.log('Disconnected. Reconnecting without FCM token (permission not granted).');
            }

            reconnectAttempts.current += 1;
            const delay = Math.min(10000, 1000 * Math.pow(2, reconnectAttempts.current - 1));
            reconnectTimeoutId.current = setTimeout(() => {
                console.log(`Attempting reconnect #${reconnectAttempts.current} after ${delay}ms...`);
                connectWebSocket();
            }, delay);
        };
    }, [deviceId, fcmToken, setProcessMessageFromApp, getFcmTokenHook]); // Add setProcessMessageFromApp to dependencies


    useEffect(() => {
        if (deviceId) {
            console.log("DeviceId is ready. Initiating WebSocket connection...");
            connectWebSocket();
        }
        return () => {
            if (wsRef.current) {
                wsRef.current.close();
            }
            clearTimeout(reconnectTimeoutId.current);
            clearTimeout(messageTimeoutId.current);
        };
    }, [deviceId, connectWebSocket]);

    const sendMessage = useCallback((message) => {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify(message));
        } else {
            console.warn("WebSocket not connected. Cannot send message:", message);
        }
    }, []);

    return {
        ws,
        connectionStatus,
        loggedIn,
        loggedInPhone,
        messages,
        isNotificationsEnabled,
        logincode,
        step,
        attemptsLeft,
        setStep,
        setLoggedInPhone,
        setMessages,
        setIsNotificationsEnabled,
        setLogincode,
        setAttemptsLeft,
        setProcessMessage: setProcessMessageFromApp, // Return the passed setter with the original name
        connectWebSocket,
        sendMessage,
        wsRef,
        messageTimeoutId
    };
};

export default useWebSocket;